/* =========================================================================
 * DataExplorer — interactive map on the Researchers page.
 *
 * Starts at world view showing area circles (250 km zones). When the user
 * zooms in past zoom 9, switches to per-epoch polylines (firstLat/Lng →
 * lastLat/Lng) for the visible area. Polylines are colored + weighted by
 * how many sensors are present in the epoch (data-diversity score) via the
 * existing `MapPolyline` component. Click any polyline → InfoWindow lists
 * every sensor that has data in that epoch.
 *
 * Wired against the live `data/location/areas/*` and per-area `markers`
 * collections (both publicly readable under existing Firestore rules).
 * No auth required.
 * ========================================================================= */

import { useEffect, useMemo, useRef, useState } from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { GoogleMap, useJsApiLoader, Circle } from "@react-google-maps/api";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { MapPolyline } from "components/Map/components/MapPolyline";
import { db } from "configuration/firebase";

// ---------------------------------------------------------------------------
// Map style — matches GlobalNetwork (Planet Labs-ish, low-clutter).
// ---------------------------------------------------------------------------

const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#475569" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }] },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{ color: "#CBD5E1" }],
  },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#F1F5F9" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#DBEAFE" }] },
];

const MARKERS_ZOOM_THRESHOLD = 9; // at/above this zoom, show per-epoch polylines

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const INITIAL_CENTER = { lat: 20, lng: 0 };
const INITIAL_ZOOM = 2;

export function DataExplorer() {
  const [areas, setAreas] = useState([]);
  const [markersByArea, setMarkersByArea] = useState({});
  // `zoom` is local state only — used to switch between circles and polylines.
  // It is NOT fed back into <GoogleMap> as a prop (that creates a feedback
  // loop: setCenter → onCenterChanged → setState → re-render → setCenter…).
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    // eslint-disable-next-line no-undef
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    id: "google-map-script",
  });

  // Load all area centers on mount
  useEffect(() => {
    let cancelled = false;
    db.collection("data")
      .doc("location")
      .collection("areas")
      .get()
      .then((snap) => {
        if (cancelled) return;
        setAreas(
          snap.docs
            .map((d) => {
              const data = d.data();
              return { id: d.id, center: data.center, distance: data.distance };
            })
            .filter((a) => a.center && typeof a.center.lat === "number")
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // When zoomed-in enough, lazy-load markers for every visible area we
  // haven't loaded yet. Avoids fetching everything at world view.
  const visibleAreaIds = useMemo(() => {
    if (zoom < MARKERS_ZOOM_THRESHOLD) return [];
    return areas.map((a) => a.id);
  }, [areas, zoom]);

  useEffect(() => {
    if (visibleAreaIds.length === 0) return;
    const toLoad = visibleAreaIds.filter((id) => !markersByArea[id]);
    if (toLoad.length === 0) return;

    let cancelled = false;
    Promise.all(
      toLoad.map((areaId) =>
        db
          .collection("data")
          .doc("location")
          .collection("areas")
          .doc(areaId)
          .collection("markers")
          .limit(200)
          .get()
          .then((snap) => [areaId, snap.docs.map((d) => d.data())])
          .catch(() => [areaId, []])
      )
    ).then((entries) => {
      if (cancelled) return;
      setMarkersByArea((prev) => {
        const next = { ...prev };
        entries.forEach(([id, data]) => {
          next[id] = data;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [visibleAreaIds, markersByArea]);

  const onAreaClick = (area) => {
    if (!mapRef.current) return;
    mapRef.current.setCenter(area.center);
    mapRef.current.setZoom(MARKERS_ZOOM_THRESHOLD + 1);
  };

  const allMarkers = useMemo(() => {
    if (zoom < MARKERS_ZOOM_THRESHOLD) return [];
    return Object.values(markersByArea).flat();
  }, [zoom, markersByArea]);

  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[200]}` })}
    >
      <Container>
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={8}>
            <MKTypography
              variant="overline"
              color="primary"
              fontWeight="bold"
              sx={{ letterSpacing: "0.1em" }}
            >
              Explore the data
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              Live coverage, route by route
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 640, mx: "auto" }}>
              Each circle is a 250 km contribution zone. Click in (or zoom past 9) to see individual
              route traces — line weight reflects how many sensor types contributed to that epoch.
              Click any route for the sensor breakdown.
            </MKTypography>
          </Grid>
        </Grid>

        <MKBox
          sx={({ palette, borders }) => ({
            position: "relative",
            borderRadius: borders.borderRadius.lg,
            overflow: "hidden",
            border: `1px solid ${palette.grey[200]}`,
            boxShadow: "0 24px 48px -24px rgba(15, 23, 42, 0.18)",
            height: { xs: 460, md: 620 },
            background: palette.grey[100],
          })}
        >
          {!isLoaded ? (
            <MKBox
              sx={{
                width: "100%",
                height: "100%",
                display: "grid",
                placeItems: "center",
                color: "text.secondary",
                fontSize: 14,
              }}
            >
              Loading map…
            </MKBox>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              options={{
                styles: MAP_STYLE,
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: "cooperative",
                minZoom: 2,
              }}
              onLoad={(map) => {
                mapRef.current = map;
                // Initial view set once, imperatively — uncontrolled from here on.
                map.setCenter(INITIAL_CENTER);
                map.setZoom(INITIAL_ZOOM);
              }}
              onZoomChanged={() => {
                if (mapRef.current) setZoom(mapRef.current.getZoom());
              }}
            >
              {zoom < MARKERS_ZOOM_THRESHOLD
                ? areas.map((a) => (
                    <Circle
                      key={a.id}
                      center={a.center}
                      radius={a.distance || 250000}
                      onClick={() => onAreaClick(a)}
                      options={{
                        fillColor: "#2563FF",
                        fillOpacity: 0.12,
                        strokeColor: "#2563FF",
                        strokeOpacity: 0.6,
                        strokeWeight: 1.5,
                        clickable: true,
                      }}
                    />
                  ))
                : allMarkers.map((m, i) => <MapPolyline marker={m} key={`${m.hash || i}-poly`} />)}
            </GoogleMap>
          )}
        </MKBox>

        <MKTypography
          variant="caption"
          color="text"
          sx={{ display: "block", textAlign: "center", mt: 3, opacity: 0.75 }}
        >
          Up to 200 routes shown per area for performance. Full datasets are delivered on inquiry.
        </MKTypography>
      </Container>
    </MKBox>
  );
}
